import { useEffect, useRef } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { MessageBubble } from './MessageBubble';
import { ChatInput } from './ChatInput';
import { TypingIndicator } from './TypingIndicator';
import { WelcomeScreen } from './WelcomeScreen';
import styles from './ChatView.module.css';

export function ChatView() {
  const {
    messages,
    activeSession,
    isSendingMessage,
    isLoadingMessages,
    error,
    sendMessage,
    clearError,
  } = useChat();

  const bottomRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSendingMessage]);

  const showWelcome = !activeSession && messages.length === 0;

  return (
    <div className={styles.chatView}>
      {/* Message area */}
      <div
        className={styles.messageArea}
        ref={listRef}
        role="log"
        aria-live="polite"
        aria-label="Conversation messages"
      >
        {showWelcome && <WelcomeScreen />}

        {isLoadingMessages && (
          <div className={styles.loadingMessages} aria-live="polite">
            <TypingIndicator label="Loading conversation…" />
          </div>
        )}

        {!isLoadingMessages &&
          messages.map((msg) => (
            <MessageBubble key={msg.messageId} message={msg} />
          ))}

        {isSendingMessage && <TypingIndicator label="AI is thinking…" />}

        <div ref={bottomRef} aria-hidden="true" />
      </div>

      {/* Error banner */}
      {error && (
        <div className={styles.errorBanner} role="alert">
          <div className={styles.errorContent}>
            <span className={styles.errorMessage}>{error.message}</span>
            {(error.code || error.requestId) && (
              <span className={styles.errorMeta}>
                {error.code && <span>Code: {error.code}</span>}
                {error.code && error.requestId && <span> · </span>}
                {error.requestId && <span>Ref: {error.requestId}</span>}
              </span>
            )}
          </div>
          <button
            onClick={clearError}
            aria-label="Dismiss error"
            className={styles.dismissBtn}
          >
            ✕
          </button>
        </div>
      )}

      {/* Input area */}
      <div className={styles.inputArea}>
        <ChatInput onSend={sendMessage} disabled={isSendingMessage || isLoadingMessages} />
      </div>
    </div>
  );
}
